import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaWallet, FaEye, FaDownload, FaCalendarAlt, FaFilter, FaChartLine, FaSortUp, FaSortDown, FaSort, FaTrophy, FaMedal, FaAward, FaCrown } from 'react-icons/fa';
import { getAllSales, getAllMitras } from '../../supabase/database';
import Spinner from '../../components/ui/Spinner';

const ManajemenKomisi = () => {
  const [sales, setSales] = useState([]);
  const [mitras, setMitras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMitra, setSelectedMitra] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [commissionSummary, setCommissionSummary] = useState({});
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' atau 'desc'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 ManajemenKomisi: Starting data fetch...');
      const [salesData, mitrasData] = await Promise.all([
        getAllSales(),
        getAllMitras()
      ]);
      console.log('📊 ManajemenKomisi: Sales data received:', salesData?.length || 0, 'records');
      console.log('👥 ManajemenKomisi: Mitras data received:', mitrasData?.length || 0, 'records');
      if (salesData && salesData.length > 0) {
        console.log('📋 Sample sales data:', salesData[0]);
      }
      setSales(salesData || []);
      setMitras(mitrasData || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Gagal mengambil data komisi');
      setSales([]);
      setMitras([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredSales = useMemo(() => {
    console.log('🔍 ManajemenKomisi filteredSales: Filtering', sales.length, 'sales with selectedMitra:', selectedMitra);
    const filtered = sales.filter(sale => {
      const mitraMatch = !selectedMitra || sale.mitra_uid === selectedMitra;
      const statusMatch = !selectedStatus || sale.status?.toLowerCase() === selectedStatus.toLowerCase();
      
      let dateMatch = true;
      if (dateFilter) {
        try {
          const saleDate = sale.createdAt instanceof Date ? sale.createdAt : new Date(sale.createdAt);
          const filterDate = new Date(dateFilter);
          dateMatch = saleDate.toDateString() === filterDate.toDateString();
        } catch (error) {
          console.error('Date comparison error:', error);
          dateMatch = true; // If error, include the sale
        }
      }
      
      return mitraMatch && statusMatch && dateMatch;
    });
    console.log('🔍 ManajemenKomisi filteredSales: After filtering, got', filtered.length, 'sales');
    return filtered;
  }, [sales, selectedMitra, selectedStatus, dateFilter]);

  const calculateCommissionSummary = useCallback(() => {
    if (!filteredSales || !mitras || filteredSales.length === 0) {
      setCommissionSummary({});
      return;
    }

    const summary = {};
    
    // Create a lookup map for mitra data
    const mitraMap = {};
    mitras.forEach(mitra => {
      if (mitra && mitra.id) {
        mitraMap[mitra.id] = mitra;
      }
    });
    
    filteredSales.forEach(sale => {
      if (!sale) return;
      
      const mitraId = sale.mitra_uid || 'unknown';
      const mitraData = mitraMap[mitraId];
      
      // Use mitra data from the mitra collection if available, otherwise fallback to sale data
      const mitraNama = mitraData?.nama || mitraData?.kode_mitra || sale.nama_mitra || 'Unknown Mitra';
      const kodeMitra = mitraData?.kode_mitra || sale.kode_mitra || 'N/A';
      // Gunakan tarif komisi global tetap 15%
      const komisiPersentase = 15;
      
      if (!summary[mitraId]) {
        summary[mitraId] = {
          nama_mitra: mitraNama,
          kode_mitra: kodeMitra,
          persentase_komisi: komisiPersentase,
          total_penjualan: 0,
          total_komisi: 0,
          jumlah_pesanan: 0,
          pesanan_lunas: 0,
          pesanan_belum_lunas: 0
        };
      }
      
      summary[mitraId].jumlah_pesanan++;
      
      // Komisi hanya dipicu berdasarkan status pembayaran "Lunas"
      const pembayaran = (sale.pembayaran || '').toLowerCase().trim();
      
      if (pembayaran === 'lunas') {
        summary[mitraId].pesanan_lunas++;
        const subtotal = Number(sale.subtotal) || Number(sale.hargaSetelahDiskon) || Number(sale.totalHarga) || 0;
        summary[mitraId].total_penjualan += subtotal;
        summary[mitraId].total_komisi += subtotal * (komisiPersentase / 100);
      } else {
        summary[mitraId].pesanan_belum_lunas++;
      }
    });
    
    setCommissionSummary(summary);
  }, [filteredSales, mitras]);

  useEffect(() => {
    if (sales.length > 0 && mitras.length > 0) {
      calculateCommissionSummary();
    }
  }, [calculateCommissionSummary]);

  const exportToCSV = useCallback(() => {
    const csvData = Object.values(commissionSummary).map(mitra => ({
      'Kode Mitra': mitra.kode_mitra,
      'Nama Mitra': mitra.nama_mitra,
      'Persentase Komisi (Global)': `15%`,
      'Total Penjualan': `Rp ${mitra.total_penjualan.toLocaleString('id-ID')}`,
      'Total Komisi': `Rp ${mitra.total_komisi.toLocaleString('id-ID')}`,
      'Jumlah Pesanan': mitra.jumlah_pesanan,
      'Pesanan Lunas': mitra.pesanan_lunas,
      'Pesanan Belum Lunas': mitra.pesanan_belum_lunas
    }));

    const csvContent = [
      Object.keys(csvData[0] || {}).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `laporan_komisi_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [commissionSummary]);

  const handleMitraChange = useCallback((e) => {
    setSelectedMitra(e.target.value);
  }, []);

  const handleStatusChange = useCallback((e) => {
    setSelectedStatus(e.target.value);
  }, []);

  const handleDateChange = useCallback((e) => {
    setDateFilter(e.target.value);
  }, []);

  const resetFilters = useCallback(() => {
    setSelectedMitra('');
    setSelectedStatus('');
    setDateFilter('');
    setSortField('');
    setSortDirection('desc');
  }, []);

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default desc direction
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField]);

  const getSortIcon = useCallback((field) => {
    if (sortField !== field) {
      return <FaSort className="text-gray-400" />;
    }
    return sortDirection === 'asc' ? 
      <FaSortUp className="text-blue-500" /> : 
      <FaSortDown className="text-blue-500" />;
  }, [sortField, sortDirection]);

  // Sorted commission summary
  const sortedCommissionSummary = useMemo(() => {
    const data = Object.values(commissionSummary);
    
    if (!sortField) {
      return data;
    }

    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'total_penjualan':
          aValue = a.total_penjualan;
          bValue = b.total_penjualan;
          break;
        case 'total_komisi':
          aValue = a.total_komisi;
          bValue = b.total_komisi;
          break;
        // Sorting berdasarkan persentase komisi dihapus (tarif global)
        case 'nama_mitra':
          aValue = a.nama_mitra.toLowerCase();
          bValue = b.nama_mitra.toLowerCase();
          break;
        case 'kode_mitra':
          aValue = a.kode_mitra.toLowerCase();
          bValue = b.kode_mitra.toLowerCase();
          break;
        case 'pesanan_lunas':
          aValue = a.pesanan_lunas;
          bValue = b.pesanan_lunas;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc' ? 
          aValue.localeCompare(bValue) : 
          bValue.localeCompare(aValue);
      } else {
        return sortDirection === 'asc' ? 
          aValue - bValue : 
          bValue - aValue;
      }
    });
  }, [commissionSummary, sortField, sortDirection]);

  const { totalAllCommissions, totalAllSales } = useMemo(() => {
    const totalCommissions = Object.values(commissionSummary).reduce((total, mitra) => total + mitra.total_komisi, 0);
    const totalSales = Object.values(commissionSummary).reduce((total, mitra) => total + mitra.total_penjualan, 0);
    return {
      totalAllCommissions: totalCommissions,
      totalAllSales: totalSales
    };
  }, [commissionSummary]);

  if (loading) return <Spinner />;

  return (
    <div className="p-4 md:p-8 bg-white min-h-full relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <FaWallet className="text-4xl text-green-500" />
          <h1 className="text-3xl font-bold text-gray-800">Manajemen Komisi</h1>
        </div>
        <button
          onClick={exportToCSV}
          disabled={loading}
          className="flex items-center gap-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded-lg transition-colors"
        >
          <FaDownload />
          Export CSV
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-blue-50 p-6 rounded-lg shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Penjualan</p>
              <p className="text-2xl font-bold text-blue-900">
                Rp {totalAllSales.toLocaleString('id-ID')}
              </p>
            </div>
            <FaChartLine className="text-3xl text-blue-500" />
          </div>
        </div>

        <div className="bg-green-50 p-6 rounded-lg shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Total Komisi</p>
              <p className="text-2xl font-bold text-green-900">
                Rp {totalAllCommissions.toLocaleString('id-ID')}
              </p>
            </div>
            <FaWallet className="text-3xl text-green-500" />
          </div>
        </div>

        <div className="bg-purple-50 p-6 rounded-lg shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Mitra Aktif</p>
              <p className="text-2xl font-bold text-purple-900">
                {Object.keys(commissionSummary).length}
              </p>
            </div>
            <FaEye className="text-3xl text-purple-500" />
          </div>
        </div>
      </div>

      {/* Top Performers Ranking - Only show if we have data */}
      {Object.keys(commissionSummary).length > 0 && (
        <div className="bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 p-6 rounded-xl shadow-lg mb-6 border border-yellow-200">
          <div className="flex items-center gap-3 mb-6">
            <FaTrophy className="text-4xl text-yellow-500" />
            <div>
              <h2 className="text-2xl font-bold text-gray-800">🏆 Ranking Pendapatan Komisi Mitra</h2>
              <p className="text-sm text-gray-600">Berdasarkan pesanan yang sudah lunas</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Top 3 by Commission */}
            <div className="bg-white rounded-lg p-5 shadow-md border border-yellow-200">
              <div className="flex items-center gap-2 mb-4">
                <FaCrown className="text-2xl text-yellow-500" />
                <h3 className="text-lg font-bold text-gray-800">Top 3 Komisi Tertinggi</h3>
              </div>
              <div className="space-y-3">
                {sortedCommissionSummary
                  .filter(mitra => mitra.total_komisi > 0)
                  .sort((a, b) => b.total_komisi - a.total_komisi)
                  .slice(0, 3)
                  .map((mitra, index) => {
                    const rankIcons = [
                      <FaTrophy className="text-yellow-500" />,
                      <FaMedal className="text-gray-400" />,
                      <FaAward className="text-orange-500" />
                    ];
                    const bgColors = ['bg-yellow-50', 'bg-gray-50', 'bg-orange-50'];
                    const borderColors = ['border-yellow-200', 'border-gray-200', 'border-orange-200'];
                    
                    return (
                      <div key={mitra.kode_mitra} 
                           className={`flex items-center justify-between p-3 rounded-lg border ${bgColors[index]} ${borderColors[index]}`}>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <span className="text-lg font-bold text-gray-700">#{index + 1}</span>
                            {rankIcons[index]}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800 text-sm">{mitra.nama_mitra}</div>
                            <div className="text-xs text-gray-600">{mitra.kode_mitra}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-green-600 text-sm">Rp {mitra.total_komisi.toLocaleString('id-ID')}</div>
                          <div className="text-xs text-gray-500">{mitra.pesanan_lunas} pesanan</div>
                        </div>
                      </div>
                    );
                  })}
                {sortedCommissionSummary.filter(mitra => mitra.total_komisi > 0).length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    <FaTrophy className="mx-auto text-3xl text-gray-300 mb-2" />
                    <p className="text-sm">Belum ada data komisi</p>
                  </div>
                )}
              </div>
            </div>

            {/* Top 3 by Sales Volume */}
            <div className="bg-white rounded-lg p-5 shadow-md border border-blue-200">
              <div className="flex items-center gap-2 mb-4">
                <FaChartLine className="text-2xl text-blue-500" />
                <h3 className="text-lg font-bold text-gray-800">Top 3 Volume Penjualan</h3>
              </div>
              <div className="space-y-3">
                {sortedCommissionSummary
                  .filter(mitra => mitra.total_penjualan > 0)
                  .sort((a, b) => b.total_penjualan - a.total_penjualan)
                  .slice(0, 3)
                  .map((mitra, index) => {
                    const rankIcons = [
                      <FaTrophy className="text-blue-500" />,
                      <FaMedal className="text-gray-400" />,
                      <FaAward className="text-blue-300" />
                    ];
                    const bgColors = ['bg-blue-50', 'bg-gray-50', 'bg-blue-25'];
                    const borderColors = ['border-blue-200', 'border-gray-200', 'border-blue-100'];
                    
                    return (
                      <div key={mitra.kode_mitra} 
                           className={`flex items-center justify-between p-3 rounded-lg border ${bgColors[index]} ${borderColors[index]}`}>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <span className="text-lg font-bold text-gray-700">#{index + 1}</span>
                            {rankIcons[index]}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800 text-sm">{mitra.nama_mitra}</div>
                            <div className="text-xs text-gray-600">{mitra.kode_mitra}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-blue-600 text-sm">Rp {mitra.total_penjualan.toLocaleString('id-ID')}</div>
                          <div className="text-xs text-gray-500">{mitra.pesanan_lunas} pesanan</div>
                        </div>
                      </div>
                    );
                  })}
                {sortedCommissionSummary.filter(mitra => mitra.total_penjualan > 0).length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    <FaChartLine className="mx-auto text-3xl text-gray-300 mb-2" />
                    <p className="text-sm">Belum ada data penjualan</p>
                  </div>
                )}
              </div>
            </div>

            {/* Most Active Mitra */}
            <div className="bg-white rounded-lg p-5 shadow-md border border-purple-200">
              <div className="flex items-center gap-2 mb-4">
                <FaEye className="text-2xl text-purple-500" />
                <h3 className="text-lg font-bold text-gray-800">Top 3 Mitra Teraktif</h3>
              </div>
              <div className="space-y-3">
                {sortedCommissionSummary
                  .filter(mitra => mitra.pesanan_lunas > 0)
                  .sort((a, b) => b.pesanan_lunas - a.pesanan_lunas)
                  .slice(0, 3)
                  .map((mitra, index) => {
                    const rankIcons = [
                      <FaTrophy className="text-purple-500" />,
                      <FaMedal className="text-gray-400" />,
                      <FaAward className="text-purple-300" />
                    ];
                    const bgColors = ['bg-purple-50', 'bg-gray-50', 'bg-purple-25'];
                    const borderColors = ['border-purple-200', 'border-gray-200', 'border-purple-100'];
                    
                    return (
                      <div key={mitra.kode_mitra} 
                           className={`flex items-center justify-between p-3 rounded-lg border ${bgColors[index]} ${borderColors[index]}`}>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <span className="text-lg font-bold text-gray-700">#{index + 1}</span>
                            {rankIcons[index]}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800 text-sm">{mitra.nama_mitra}</div>
                            <div className="text-xs text-gray-600">{mitra.kode_mitra}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-purple-600 text-sm">{mitra.pesanan_lunas} pesanan</div>
                          <div className="text-xs text-gray-500">Rp {mitra.total_komisi.toLocaleString('id-ID')}</div>
                        </div>
                      </div>
                    );
                  })}
                {sortedCommissionSummary.filter(mitra => mitra.pesanan_lunas > 0).length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    <FaEye className="mx-auto text-3xl text-gray-300 mb-2" />
                    <p className="text-sm">Belum ada data pesanan lunas</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white bg-opacity-70 rounded-lg p-4 text-center border border-yellow-200">
              <div className="text-2xl font-bold text-yellow-600">
                {sortedCommissionSummary.filter(m => m.total_komisi > 0).length}
              </div>
              <div className="text-sm text-gray-600">Mitra Berpendapatan</div>
            </div>
            <div className="bg-white bg-opacity-70 rounded-lg p-4 text-center border border-green-200">
              <div className="text-2xl font-bold text-green-600">
                Rp {sortedCommissionSummary.reduce((sum, m) => sum + m.total_komisi, 0).toLocaleString('id-ID')}
              </div>
              <div className="text-sm text-gray-600">Total Komisi Lunas</div>
            </div>
            <div className="bg-white bg-opacity-70 rounded-lg p-4 text-center border border-blue-200">
              <div className="text-2xl font-bold text-blue-600">
                {sortedCommissionSummary.reduce((sum, m) => sum + m.pesanan_lunas, 0)}
              </div>
              <div className="text-sm text-gray-600">Total Pesanan Lunas</div>
            </div>
            <div className="bg-white bg-opacity-70 rounded-lg p-4 text-center border border-purple-200">
              <div className="text-2xl font-bold text-purple-600">
                {Math.round(sortedCommissionSummary.reduce((sum, m) => sum + m.total_komisi, 0) / Math.max(sortedCommissionSummary.reduce((sum, m) => sum + m.pesanan_lunas, 0), 1)).toLocaleString('id-ID')}
              </div>
              <div className="text-sm text-gray-600">Rata-rata Komisi/Pesanan</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FaFilter className="text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-800">Filter Data</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pilih Mitra</label>
            <select
              value={selectedMitra}
              onChange={handleMitraChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Semua Mitra</option>
              {mitras.map(mitra => (
                <option key={mitra.id} value={mitra.id}>
                  {mitra.kode_mitra} - {mitra.nama || mitra.kode_mitra || 'N/A'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status Pesanan</label>
            <select
              value={selectedStatus}
              onChange={handleStatusChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Semua Status</option>
              <option value="menunggu pembayaran">Menunggu Pembayaran</option>
              <option value="dibayar">Dibayar</option>
              <option value="diproses">Diproses</option>
              <option value="dikirim sebagian">Dikirim Sebagian</option>
              <option value="dikirim seluruhnya">Dikirim Seluruhnya</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tanggal</label>
            <input
              type="date"
              value={dateFilter}
              onChange={handleDateChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={resetFilters}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
          >
            Reset Filter
          </button>
        </div>
      </div>

      {/* Commission Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden relative z-0">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-700">Ringkasan Komisi per Mitra</h2>
            <div className="text-sm text-gray-500">
              💡 Klik header kolom untuk mengurutkan data
              {sortField && (
                <span className="ml-2 text-blue-600 font-medium">
                  (Diurutkan berdasarkan: {sortField === 'total_penjualan' ? 'Total Penjualan' : 
                                          sortField === 'total_komisi' ? 'Total Komisi' :
                                          sortField === 'nama_mitra' ? 'Nama Mitra' :
                                          sortField === 'kode_mitra' ? 'Kode Mitra' :
                                          sortField === 'pesanan_lunas' ? 'Pesanan Lunas' : sortField} 
                  - {sortDirection === 'asc' ? 'A-Z/Terkecil' : 'Z-A/Terbesar'})
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('kode_mitra')}
                >
                  <div className="flex items-center gap-2">
                    Kode Mitra
                    {getSortIcon('kode_mitra')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('nama_mitra')}
                >
                  <div className="flex items-center gap-2">
                    Nama Mitra
                    {getSortIcon('nama_mitra')}
                  </div>
                </th>
                {/* Kolom komisi per mitra dihapus (tarif global) */}
                <th 
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('total_penjualan')}
                >
                  <div className="flex items-center justify-end gap-2">
                    Total Penjualan
                    {getSortIcon('total_penjualan')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('total_komisi')}
                >
                  <div className="flex items-center justify-end gap-2">
                    Total Komisi
                    {getSortIcon('total_komisi')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('pesanan_lunas')}
                >
                  <div className="flex items-center justify-center gap-2">
                    Pesanan Lunas
                    {getSortIcon('pesanan_lunas')}
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pesanan Belum Lunas</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedCommissionSummary.length > 0 ? sortedCommissionSummary.map((mitra, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{mitra.kode_mitra}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{mitra.nama_mitra}</td>
                  {/* Persentase komisi per mitra tidak ditampilkan */}
                  <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                    Rp {mitra.total_penjualan.toLocaleString('id-ID')}
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-bold text-green-600">
                    Rp {mitra.total_komisi.toLocaleString('id-ID')}
                  </td>
                  <td className="px-6 py-4 text-sm text-center">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                      {mitra.pesanan_lunas}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-center">
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                      {mitra.pesanan_belum_lunas}
                    </span>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    <FaWallet className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p>Tidak ada data komisi yang ditemukan</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManajemenKomisi;
